import type { Account } from '../types'

import { ProductName, PRODUCTS } from '../constants/products'
import type { ProductType } from '../constants/products'
import { getUrl, parseBibeHostname } from '@buffer-mono/shared-utils/bibe'
import { getPublishUrl } from '@buffer-mono/shared-utils/urls'

import { getActiveProduct } from '../../exports/Navigator/utils/getActiveProductFromUrl'

export function getProductUrl(product: ProductType): string {
  const { bibePrefix, effectiveHostname, envModifier } = parseBibeHostname()
  const currentProductDetails: ProductType | null = getActiveProduct()
  const currentProductBaseSubdomain =
    currentProductDetails?.subdomain || effectiveHostname.split('.')[0]

  let targetOriginalServiceHostname = effectiveHostname
  if (
    currentProductBaseSubdomain &&
    product.subdomain !== currentProductBaseSubdomain
  ) {
    targetOriginalServiceHostname = effectiveHostname.replace(
      currentProductBaseSubdomain,
      product.subdomain,
    )
  } else if (!currentProductBaseSubdomain) {
    targetOriginalServiceHostname = effectiveHostname.replace(
      effectiveHostname.split('.')[0],
      product.subdomain,
    )
  }
  let finalTargetHostname = `${bibePrefix}${targetOriginalServiceHostname}`
  if (product.id === ProductName.ACCOUNT) {
    finalTargetHostname = `${targetOriginalServiceHostname}`
  }
  const port = envModifier === 'local.' ? product.localPort : ''
  const path = product.pathname ?? ''
  return path !== ''
    ? `https://${finalTargetHostname}${port}/${path}`
    : `https://${finalTargetHostname}${port}`
}

export type ComposerUrlArgs = {
  socialChannelId: string
  cta: string
}

export function getPublishComposerURL({
  socialChannelId,
  cta,
}: ComposerUrlArgs): string {
  const baseUrl = getProductUrl(PRODUCTS[ProductName.PUBLISH])
  return `${baseUrl}/channels/${socialChannelId}/?tab=queue&auto_open_composer=true&profile_id=${socialChannelId}&cta=${cta}`
}

export function getPublishComposerDraftURL({
  socialChannelId,
  cta,
}: ComposerUrlArgs): string {
  const baseUrl = getProductUrl(PRODUCTS[ProductName.PUBLISH])
  return `${baseUrl}/channels/${socialChannelId}?tab=queue&auto_open_composer_as_draft=true&profile_id=${socialChannelId}&cta=${cta}`
}

export function getPostingScheduleURL(args: { profileId: string }): string {
  const { profileId } = args
  const baseUrl = getProductUrl(PRODUCTS[ProductName.PUBLISH])
  return `${baseUrl}/channels/${profileId}/settings?tab=posting-schedule`
}

export function getBillingPageUrl(cta: string | null): string {
  const baseUrl = getProductUrl(PRODUCTS[ProductName.PUBLISH])
  const ctaParam = cta ? `/?cta=${cta}` : ``
  return `${baseUrl}/settings/billing${ctaParam}`
}

export function getBillingPaymentMethodUrl(): string {
  const baseUrl = getProductUrl(PRODUCTS[ProductName.PUBLISH])
  return `${baseUrl}/settings/billing/#payment-method`
}

export function getAccountBaseUrl(): string {
  const baseUrl = getProductUrl(PRODUCTS[ProductName.ACCOUNT])
  return baseUrl
}

export function getAccountUrl(subpath = ''): string {
  const baseUrl = getProductUrl(PRODUCTS[ProductName.ACCOUNT])
  return `${baseUrl}/${subpath}`
}

export function getNewSettingsUrl(subpath = ''): string {
  const baseUrl = getProductUrl(PRODUCTS[ProductName.NEWSETTINGS])
  return `${baseUrl}/${subpath}`
}

export function redirectToUrl(redirectURI: string): void {
  window.location.assign(redirectURI)
}

export function reloadPage(): void {
  window.location.reload()
}

export function getStartPageUrl(subpath = ''): string {
  const baseUrl = getProductUrl(PRODUCTS[ProductName.START_PAGE])
  return `${baseUrl}/${subpath}`
}

export function isInPublish(): boolean {
  const hostname = window.location.hostname

  return hostname.includes('publish')
}

export function isOnPublishChannelsRoute(): boolean {
  const isPublishProduct = isInPublish()

  if (!isPublishProduct) return false

  const pathname = window.location.pathname
  return pathname.includes('/channels')
}

export function isOnPublishProfileRoute(): boolean {
  const isPublishProduct = isInPublish()

  if (!isPublishProduct) return false

  const pathname = window.location.pathname
  return pathname.includes('/profile')
}

export function isOnPublishAllChannelsRoute(): boolean {
  const isPublishProduct = isInPublish()

  if (!isPublishProduct) return false

  const pathname = window.location.pathname
  return pathname.includes('all-channels')
}

export function isOnPublishCalendarRoute(): boolean {
  const isPublishProduct = isInPublish()

  if (!isPublishProduct) return false

  const pathname = window.location.pathname
  return pathname.includes('calendar')
}

export function isOnPublishInsightsRoute(): boolean {
  const isPublishProduct = isInPublish()

  if (!isPublishProduct) return false

  const pathname = window.location.pathname
  return pathname.includes('/insights')
}

export function isInAccount(): boolean {
  const hostname = window.location.hostname

  return hostname.includes('account')
}

export function isOnAccountChannelsRoute(): boolean {
  const isAccountProduct = isInAccount()

  if (!isAccountProduct) {
    return false
  }

  const pathname = window.location.pathname
  return pathname.includes('channels')
}

export function getSelectedChannelIdFromProfileRoute(): string {
  const pathname = window.location.pathname
  return pathname.split('/')[2]
}

export function getSelectedChannelIdFromChannelsRoute(): string {
  const pathWithoutQueryString = window.location.pathname.split('?')[0]
  return pathWithoutQueryString.split('/')[2]
}

export function getSelectedChannelIdFromInsightsRoute(): string {
  const pathWithoutQueryString = window.location.pathname.split('?')[0]
  return pathWithoutQueryString.split('/')[2]
}

export function getSelectedChannelIdFromAllChannelsRoute(): string[] {
  const urlParams = new URLSearchParams(window.location.search)

  return urlParams.getAll('channels[]')
}

export function getSelectedChannelsFromPublishingRoute(): string[] {
  const isProfileRoute = isOnPublishProfileRoute()
  const isChannelsRoute = isOnPublishChannelsRoute()
  const isAllChannelsRoute = isOnPublishAllChannelsRoute()
  const isCalendarRoute = isOnPublishCalendarRoute()
  const isInsightsRoute = isOnPublishInsightsRoute()
  const shouldAttempttoGetProfileIdFromURL =
    isProfileRoute ||
    isChannelsRoute ||
    isAllChannelsRoute ||
    isCalendarRoute ||
    isInsightsRoute

  if (!shouldAttempttoGetProfileIdFromURL) return []

  if (isProfileRoute) {
    const selectedChannelId = getSelectedChannelIdFromProfileRoute()
    return selectedChannelId ? [selectedChannelId] : []
  }

  if (isChannelsRoute) {
    const selectedChannelId = getSelectedChannelIdFromChannelsRoute()
    return selectedChannelId ? [selectedChannelId] : []
  }

  if (isInsightsRoute) {
    const selectedChannelId = getSelectedChannelIdFromInsightsRoute()
    return selectedChannelId ? [selectedChannelId] : []
  }

  if (isAllChannelsRoute || isCalendarRoute) {
    const selectedChannelIds = getSelectedChannelIdFromAllChannelsRoute()
    return selectedChannelIds
  }

  return []
}

export function isInAnalyze(): boolean {
  const hostname = window.location.hostname

  return hostname.includes('analyze')
}

export function isOnAnalyzeOverviewRoute(): boolean {
  const isInAnalyzeProduct = isInAnalyze()

  if (!isInAnalyzeProduct) return false

  const pathname = window.location.pathname
  return pathname.includes('overview')
}

export function getSelectedChannelIdFromOverviewRoute(): string {
  const pathname = window.location.pathname
  return pathname.split('/')[3]
}

export function getSelectedChannelsFromAnalyzeRoute(): string[] {
  const isOverviewRoute = isOnAnalyzeOverviewRoute()

  if (!isOverviewRoute) return []

  const selectedChannelId = getSelectedChannelIdFromOverviewRoute()

  return selectedChannelId ? [selectedChannelId] : []
}

export function isInStartPage(): boolean {
  const hostname = window.location.hostname

  return hostname.includes('start-page')
}

export function getSelectedChannelIdFromStartPageRoute(): string | null {
  const pathname = window.location.pathname
  const channelId = pathname.split('/')[1]

  // Onboarding is the only other value which can be found in the first position of the path
  if (channelId === 'onboarding') return null

  return channelId
}

export function getSelectedChannelsFromStartPageRoute(): string[] {
  const isStartPageRoute = isInStartPage()

  if (!isStartPageRoute) return []

  const selectedChannelId = getSelectedChannelIdFromStartPageRoute()

  return selectedChannelId ? [selectedChannelId] : []
}

export function isInEngage(): boolean {
  const hostname = window.location.hostname

  return hostname.includes('engage')
}

export function getSelectedChannelIdFromEngageRoute(): string {
  const pathname = window.location.pathname
  return pathname.split('/')[1]
}

export function getSelectedChannelsFromEngageRoute(): string[] {
  const isInEngageProduct = isInEngage()

  if (!isInEngageProduct) return []

  const selectedChannelId = getSelectedChannelIdFromEngageRoute()

  return selectedChannelId ? [selectedChannelId] : []
}

export function getTeamManageUrl(
  user: Pick<Account, 'currentOrganization'>,
  subpath = '',
): string {
  const orgId = user.currentOrganization?.id

  return orgId
    ? getUrl(`buffer.com/manage/${orgId}/team-members/${subpath}`)
    : getUrl('buffer.com').replace(/\/$/, '')
}

export function getManageUrl(): string {
  return getUrl('buffer.com/manage/team-members/')
}

export function getTeamInviteUrl(user: Account): string {
  return getTeamManageUrl(user, 'invite')
}

export function getBillingUrl(): string {
  return getAccountUrl('billing')
}

export function getManageChannelsURL(): string {
  return getPublishUrl('settings/channels')
}

export function getChannelsQueueUrl({
  channelId,
}: {
  channelId: string
}): string {
  return getPublishUrl(`channels/${channelId}?tab=queue`)
}

export function getChannelSettingsUrl({
  channelId,
}: {
  channelId: string
}): string {
  return getPublishUrl(`channels/${channelId}/settings`)
}

export function getStopImpersonationUrl(): string | null {
  const { hostname } = window.location

  if (!hostname.endsWith('buffer.com') && hostname !== 'localhost') {
    return null
  }

  return `https://admin${
    hostname.includes('local') ? '-next.local' : ''
  }.buffer.com/clearImpersonation`
}

export function getGlobalActionCTAFromQueryParams(): string | null {
  const urlParams = new URLSearchParams(window.location.search)

  return urlParams.get('globalActionCTA')
}
